import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserRound, LogOut, Search } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import AlertScadenzeMedici from './AlertScadenzeMedici';
import AlertNotifier from './AlertNotifier';
import RitardiNotifier from './RitardiNotifier';
import AttesaNotifier from './AttesaNotifier';
import { PostiAvvisi } from './postiAvvisi';
import { CodaFinestre, useStatoCoda } from './codaFinestre';
import '../styles/Layout.css';
import Assistenza from '../pages/Assistenza';
import { PopupCompiti } from '../pages/Compiti';
import {
    vociMenu, voceAccesa, linguetteDi, percorsoDi, azioniDi,
} from '../navigazione/schermate';
import BarraInAlto from './guscio/BarraInAlto';
import BarraComandi from './guscio/BarraComandi';
import useContatori from '../hooks/useContatori';
import useMarchio from '../hooks/useMarchio';
import { useRuoli, etichettaRuolo } from '../utils/ruoli';

/**
 * Layout — il guscio comune a tutte le schermate (stile «Console», fase 1,
 * Alex 2/10/2026; docs/proposte/26-stile-console.md, mockup «Sidebar» e
 * «Main»).
 *
 *   - a sinistra il MENU SCURO, largo 232 px: il marchio, «Cerca o
 *     esegui» (la barra comandi, Ctrl K), le voci in quattro gruppi e in
 *     fondo l'utente, con «Il mio account» ed «Esci»;
 *   - in alto la BARRA (guscio/BarraInAlto.jsx): dove si e', lo stato dei
 *     controlli automatici, gli avvisi col loro numero, «Nuovo»;
 *   - sotto gli 820 px il menu e' un cassetto che si apre dalla barra, con
 *     le stesse voci e gli stessi gruppi. Fino al 2/10 diventava una
 *     striscia orizzontale di voci senza gruppi: con 33 voci per
 *     l'amministrazione, una fila da far scorrere col dito.
 *
 * Le voci del menu NON si scrivono qui: vengono dalla tabella
 * navigazione/schermate.js (vociMenu), la stessa da cui App.js costruisce
 * le rotte, gia' filtrate per ruolo con la guardia delle rotte. Una voce
 * nuova e' una riga di quella tabella. `schermata` e' la riga della
 * schermata aperta: la passa App.js.
 *
 * Le icone sono disegni monocromatici, non emoji: le emoji portano
 * colori propri che ignorano la palette, cambiano forma da un sistema
 * all'altro e non si possono allineare con precisione. Qui prendono il
 * colore dal testo che accompagnano.
 *
 * I nomi delle classi di prima restano dove fanno la stessa cosa
 * (.header, .sidebar, .menu-item, .user-info, .btn-logout...): le prove li
 * cercano (verificaRotte, provaAngoloAvvisi, navigazione.test) e PilaAvvisi
 * apre il suo pannello sotto `.header`. Quelli nuovi hanno il prefisso
 * `guscio-`. Tutte le regole stanno in Layout.css e in nessun altro foglio:
 * fino al 2/10 Dashboard.css ridefiniva .header, .user-info e .btn-logout, e
 * vinceva Layout.css solo per l'ordine in cui si caricavano.
 */

const Layout = ({ schermata, children }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const user = useAuthStore((state) => state.user);
    const logout = useAuthStore((state) => state.logout);
    const { ruoli } = useRuoli();

    /* I posti degli avvisi che arrivano da soli (components/postiAvvisi.js):
       la colonna in basso a destra e il posto nella barra. */
    const [angolo, setAngolo] = useState(null);
    const [barra, setBarra] = useState(null);
    /* Le finestre che si aprono da sole: una alla volta (codaFinestre.js).
       Finche' una e' aperta l'angolo non si offre a nessuno, cosi' il
       Promemoria e la pila non ne coprono i pulsanti. */
    const coda = useStatoCoda();

    /* Il Promemoria dell'angolo e la campanella della barra: il numero lo
       dice AlertNotifier (l'elenco che sta mostrando), l'apertura sta qui
       perche' la campanella la possa chiedere. */
    const [promemoriaAperto, setPromemoriaAperto] = useState(false);
    const [quantiPromemoria, setQuantiPromemoria] = useState(0);
    const promemoria = useMemo(() => ({
        aperto: promemoriaAperto, apri: setPromemoriaAperto, segnala: setQuantiPromemoria,
    }), [promemoriaAperto]);

    /* Il cassetto del menu, sotto gli 820 px. Sopra non conta: il CSS
       ignora la classe e il menu sta sempre al suo posto. */
    const [menuAperto, setMenuAperto] = useState(false);
    const menu = useRef(null);

    /* La pagina lascia in fondo uno spazio alto quanto l'angolo, finche'
       ci sono avvisi: scorrendo, qualunque pulsante esce da sotto. Si
       misura l'angolo intero (Promemoria e pila insieme), e a scorrere e'
       la finestra, per questo la variabile sta sul documento. */
    useEffect(() => {
        if (!angolo || typeof ResizeObserver === 'undefined') return undefined;
        const radice = document.documentElement;
        const misura = () => {
            const h = Math.ceil(angolo.getBoundingClientRect().height);
            if (h > 0) radice.style.setProperty('--rm-spazio-pila', `${h + 108}px`);
            else radice.style.removeProperty('--rm-spazio-pila');
        };
        const o = new ResizeObserver(misura);
        o.observe(angolo);
        misura();
        return () => { o.disconnect(); radice.style.removeProperty('--rm-spazio-pila'); };
    }, [angolo]);

    /* Una voce scelta porta altrove: il cassetto si chiude da solo, come
       si chiude un menu di telefono. */
    useEffect(() => { setMenuAperto(false); }, [pathname]);
    /* ...e anche quando il percorso NON cambia (revisione del 6/10/2026): la
       voce Pazienti cliccata dall'elenco filtrato (/pazienti?filtro=...)
       porta all'elenco intero restando su /pazienti, e il cassetto restava
       aperto sopra l'elenco. E' la strada naturale per tornare indietro dal
       numero accanto alla voce, che il cassetto lo chiude da se'. */
    const scegliVoce = (percorso) => { setMenuAperto(false); navigate(percorso); };

    /* Cassetto aperto: il fuoco entra nel menu (chi usa la tastiera ci si
       ritrova dentro), Esc lo chiude e riporta il fuoco al pulsante che
       l'ha aperto. */
    useEffect(() => {
        if (!menuAperto) return undefined;
        const primo = menu.current?.querySelector('.menu-item.active') || menu.current?.querySelector('.menu-item');
        if (primo) primo.focus();
        const esc = (e) => {
            if (e.key !== 'Escape') return;
            setMenuAperto(false);
            document.querySelector('.guscio-apri-menu')?.focus();
        };
        document.addEventListener('keydown', esc);
        return () => document.removeEventListener('keydown', esc);
    }, [menuAperto]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    /* La campanella: con promemoria arrivati apre il Promemoria
       dell'angolo e ci porta il fuoco; senza (o con l'angolo occupato da
       una finestra) apre la schermata dei Promemoria, dove ci sono tutti. */
    const apriAvvisi = () => {
        const testa = document.querySelector('.rm-angolo .al-notif-titolo-testa');
        if (quantiPromemoria > 0 && testa) {
            setPromemoriaAperto(true);
            testa.focus();
        } else {
            navigate('/promemoria');
        }
    };

    /* Il menu a quattro gruppi (Lavoro, Clinica, Amministrazione, Sistema)
       e, per ogni gruppo, le voci che la rotta apre a questo utente. Il
       perche' di ogni voce - a chi serve, perche' sta in quel gruppo - e'
       scritto accanto alla sua riga in navigazione/schermate.js. */
    const gruppi = vociMenu(user);
    /* Si accende anche stando su una schermata di dettaglio o su una
       linguetta: sulla cartella di un paziente, «Pazienti». */
    const accesa = voceAccesa(schermata);
    const linguette = linguetteDi(schermata, user);
    /* I numeri accanto alle voci (hooks/useContatori.js): una chiave che
       manca vuol dire nessun numero, e zero non si scrive. */
    const contatori = useContatori();
    /* Il nome in cima al menu e la sua parte in evidenza, dal parametro
       della struttura (hooks/useMarchio.js); null finche' non arriva. */
    const marchio = useMarchio();
    const [comandiAperti, setComandiAperti] = useState(false);
    const apriComandi = () => { setMenuAperto(false); setComandiAperti(true); };

    const iniziali = `${user?.nome?.charAt(0) || ''}${user?.cognome?.charAt(0) || ''}`;

    return (
        <CodaFinestre.Provider value={coda}>
        <PostiAvvisi.Provider value={{ angolo: coda.turno ? null : angolo, barra, promemoria }}>
        <div className={`layout ${menuAperto ? 'layout--menu-aperto' : ''}`}>
            <AlertScadenzeMedici user={user} />
            <AlertNotifier />
            {/* I ritardi li vede chi accoglie, non chi visita: un medico
                che sta in stanza non puo' fare niente di quello che questo
                riquadro propone - scrivere, telefonare, far accomodare - e
                un avviso che non si puo' agire e' solo un disturbo. */}
            {(user?.ruolo === 'admin' || user?.ruolo === 'segreteria') && <RitardiNotifier />}
            {/* Al medico va l'altra meta' della stessa giornata: non chi
                manca, ma chi e' gia' qui e sta aspettando lui. */}
            {(user?.ruolo === 'medico' || user?.ruolo === 'medico_senior') && <AttesaNotifier />}
            <PopupCompiti />

            <div className="layout-body">
                <aside className="sidebar" id="guscio-menu" ref={menu}>
                    <div className="logo-area">
                        {/* Il marchio e' testo, non un'immagine (Alex,
                            4/10/2026: il nome in bianco in Montserrat, la
                            parte finale in evidenza, nessuna immagine finche'
                            non c'e' il logo). Il NOME non e' scritto qui: e'
                            il parametro della struttura (Alex, 6/10/2026,
                            risposta 21), che arriva da useMarchio; la parte in
                            evidenza la ricava una regola sola dalla forma del
                            nome (utils/marchio.js: le maiuscole in fondo, se
                            almeno due e precedute da una minuscola), e senza
                            parametro il nome e' il ripiego neutro, senza
                            evidenza. Mentre il nome arriva il posto resta
                            vuoto, alto una riga, perche' il menu non salti.
                            Testo vero, senza aria-label ne' ruoli: il lettore
                            di schermo legge il nome come lo legge chi guarda,
                            e lo prova provaGuscio. Fuori dal menu non cambia
                            niente: la schermata di accesso ha il suo file del
                            logo, le fatture e le attestazioni in PDF il loro,
                            e il tablet scrive il nome della struttura, come
                            prima. */}
                        <span className="guscio-marchio">
                            {marchio ? marchio.corpo : '\u00a0'}
                            {marchio && marchio.evidenza && <span className="guscio-marchio-evidenza">{marchio.evidenza}</span>}
                        </span>
                    </div>
                    {/* La barra comandi (Ctrl K): schermate, azioni, pazienti.
                        Il tasto si scrive anche qui, perche' una scorciatoia
                        che nessuno vede scritta non la usa nessuno.
                        data-cerca-o-esegui: il patto con le prove
                        (scripts/percorsi/guscio.js), che aprono la barra da
                        qui col mouse, come chi non conosce Ctrl K. */}
                    <button type="button" className="guscio-cerca" onClick={apriComandi}
                            data-cerca-o-esegui=""
                            title="Cerca una schermata, un'azione o un paziente">
                        <Search size={15} strokeWidth={2} aria-hidden="true" />
                        <span className="guscio-cerca-testo">Cerca o esegui…</span>
                        <kbd className="guscio-tasto">Ctrl K</kbd>
                    </button>
                    {/* «Menu» e' il nome del nav delle voci, non dell'aside che
                        le contiene: e' il patto con le prove
                        (scripts/percorsi/guscio.js), che trovano le voci per
                        attributo - nav[aria-label="Menu"] [data-percorso] - e
                        non per classe, cosi' il disegno puo' cambiare. */}
                    <nav className="guscio-voci" aria-label="Menu">
                        {gruppi.map((g) => (
                            <div key={g.codice} className="nav-gruppo" role="group" aria-labelledby={`nav-gruppo-${g.codice}`}>
                                <div className="nav-gruppo-titolo" id={`nav-gruppo-${g.codice}`}>{g.etichetta}</div>
                                {g.voci.map(({ percorso, Icona, etichetta, contatore, contatoreApre }) => {
                                    /* Il numero, solo se e' piu' di zero. Se e' un
                                       collegamento suo (contatoreApre nella tabella:
                                       oggi Pazienti, Alex 6/10/2026, risposta 22)
                                       sta ACCANTO alla voce, fratello e non figlio:
                                       un collegamento dentro una voce che si clicca
                                       e' un elemento interattivo annidato - la
                                       tastiera e il lettore di schermo non sanno
                                       quale dei due e', e il clic arriva a tutti e
                                       due. Si posa sul margine destro della voce,
                                       che gli lascia il posto: a vederlo sta dove
                                       stava. */
                                    const numero = contatore && contatori[contatore] > 0 ? contatori[contatore] : null;
                                    const accanto = numero !== null && !!contatoreApre;
                                    return (
                                        <div key={percorso} className="menu-riga">
                                            <div
                                                className={`menu-item ${accesa === percorso ? 'active' : ''} ${accanto ? 'menu-item--numero-accanto' : ''}`}
                                                data-percorso={percorso}
                                                onClick={() => scegliVoce(percorso)}
                                                role="button"
                                                tabIndex={0}
                                                title={etichetta}
                                                aria-current={accesa === percorso ? 'page' : undefined}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scegliVoce(percorso); } }}
                                            >
                                                <span className="menu-icon">
                                                    <Icona size={17} strokeWidth={1.8} aria-hidden="true" />
                                                </span>
                                                <span className="menu-label">{etichetta}</span>
                                                {numero !== null && !accanto && (
                                                    /* data-contatore e data-voce: il patto delle
                                                       prove col guscio (scripts/percorsi/guscio.js),
                                                       che non cercano le classi del disegno. */
                                                    <span className={`nav-contatore nav-contatore--${contatore} rm-numeri`} data-contatore={contatore} data-voce={percorso}>{numero}</span>
                                                )}
                                            </div>
                                            {accanto && (
                                                /* Niente data-percorso: non e' una voce (lo
                                                   scenario 16 conta le voci da quell'attributo).
                                                   Il clic chiude il cassetto anche se il
                                                   percorso non cambia (gia' su /pazienti, il
                                                   filtro e' nella ricerca dell'indirizzo). */
                                                <Link
                                                    to={contatoreApre.indirizzo}
                                                    className={`nav-contatore nav-contatore--${contatore} nav-contatore--collegamento rm-numeri`}
                                                    data-contatore={contatore}
                                                    data-voce={percorso}
                                                    aria-label={contatoreApre.descrivi(numero)}
                                                    title={contatoreApre.descrivi(numero)}
                                                    onClick={() => setMenuAperto(false)}
                                                >
                                                    {numero}
                                                </Link>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </nav>
                    {/* L'utente sta in fondo al menu. «Il mio account» e' scritto
                        e non solo cliccabile sul nome: serve a tutti i ruoli -
                        da li' si cambia la password e si attiva la verifica in
                        due passaggi, obbligatoria per amministrazione e
                        segreteria - e un nome cliccabile senza un'etichetta
                        non lo scopre nessuno. */}
                    <div className="user-info">
                        <button type="button" className="user-info-link" onClick={() => navigate('/profilo')}
                                title="Il mio account">
                            <span className="user-avatar" aria-hidden="true">{iniziali}</span>
                            <span className="guscio-utente-testo">
                                <span className="user-name rm-tronca" title={`${user?.nome || ''} ${user?.cognome || ''}`.trim()}>{user?.nome} {user?.cognome}</span>
                                <span className="user-role">{etichettaRuolo(ruoli, user?.ruolo)}</span>
                            </span>
                        </button>
                        <div className="guscio-utente-azioni">
                            <button type="button" onClick={() => navigate('/profilo')} className="btn-profilo">
                                <UserRound size={14} strokeWidth={1.8} aria-hidden="true" />
                                <span>Il mio account</span>
                            </button>
                            <button type="button" onClick={handleLogout} className="btn-logout">
                                <LogOut size={14} strokeWidth={1.8} aria-hidden="true" />
                                <span>Esci</span>
                            </button>
                        </div>
                    </div>
                </aside>

                <div className="guscio-colonna">
                    <BarraInAlto
                        percorso={percorsoDi(schermata, user)}
                        utente={user}
                        azioni={azioniDi(user)}
                        contatori={contatori}
                        onApriComandi={apriComandi}
                        postoAvvisi={setBarra}
                        avvisi={{ quanti: quantiPromemoria, onApri: apriAvvisi }}
                        menuAperto={menuAperto}
                        onApriMenu={() => setMenuAperto((x) => !x)}
                    />
                    <div className="main-content">
                        {/* Le schermate che stanno insieme sotto una voce sola:
                            Compiti e Promemoria, Testi e Ricette, Catalogo e Voci
                            con scadenza (Alex, 2/10/2026). Senza queste linguette
                            Promemoria e Voci con scadenza non avrebbero nessuna
                            porta: non c'e' un altro collegamento che ci porti. */}
                        {linguette.length > 0 && (
                            <nav className="nav-linguette" aria-label="Schermate di questa voce">
                                {linguette.map((l) => (
                                    <button
                                        key={l.percorso}
                                        type="button"
                                        className={`nav-linguetta ${l === schermata ? 'nav-linguetta--attiva' : ''}`}
                                        data-percorso={l.percorso}
                                        aria-current={l === schermata ? 'page' : undefined}
                                        onClick={() => { if (l !== schermata) navigate(l.percorso); }}
                                    >
                                        {l.linguetta || l.etichetta}
                                    </button>
                                ))}
                            </nav>
                        )}
                        {children}
                    </div>
                </div>
            </div>
            {/* Dietro il cassetto aperto, sul telefono: un tocco fuori lo chiude. */}
            <div className="guscio-velo" aria-hidden="true" onClick={() => setMenuAperto(false)} />
        <Assistenza />
        <BarraComandi
            utente={user}
            aperta={comandiAperti}
            onApri={apriComandi}
            onChiudi={() => setComandiAperti(false)}
        />
        {/* La colonna in basso a destra: Promemoria e pila, uno sopra
            l'altro, sopra il pulsante dell'assistente. */}
        <div className="rm-angolo" ref={setAngolo} />
        </div>
        </PostiAvvisi.Provider>
        </CodaFinestre.Provider>
    );
};

export default Layout;
